import { formatAddress as formatAddressWithoutCountry } from 'localized-address-format';

import { BusinessAccountsRequest, BusinessLocationsRequest, Address } from '../../api/types';
import { SelectionListItem } from '../../contexts/types';

const getBusinessAccountId = (accountName: string): string => {
    // the format of the account name is "accounts/1234567890"
    return accountName.replace(/^accounts\//, '');
};
const getBusinessLocationId = (accountName: string): string => {
    // the format of the location name is "locations/1234567890"
    return accountName.replace(/^locations\//, '');
};

const convertBusinessAccountsData = ({
    businessAccounts,
    selectedAccounts,
}: {
    businessAccounts: BusinessAccountsRequest[];
    selectedAccounts?: SelectionListItem[];
}): SelectionListItem[] => {
    return businessAccounts
        .map((accounts) => {
            return accounts.accounts.map((account) => {
                return {
                    title: account.accountName,
                    subtitle: `ID: ${getBusinessAccountId(account.name)}`,
                    id: getBusinessAccountId(account.name),
                    ...(selectedAccounts && {
                        checked: selectedAccounts
                            .map((account) => account.id)
                            .includes(getBusinessAccountId(account.name)),
                    }),
                    alreadyGrantedAccess: account.alreadyGrantedAccess,
                    canGrantAccess: account.canGrantAccess,
                    type: account.type,
                };
            });
        })
        .flat();
};

const formatAddress = (address: Address, translations: Record<string, string>): string => {
    const addressWithPostalCountry = {
        ...address,
    };
    const formattedAddressWithoutCountry =
        formatAddressWithoutCountry(addressWithPostalCountry).join('\n');

    const countryPopulated =
        translations[`country.name.${address.regionCode?.toLowerCase()}`] || '';

    return `${formattedAddressWithoutCountry}, ${countryPopulated}`;
};

const convertBusinessLocationsData = ({
    businessLocations,
    selectedLocations,
    translations,
}: {
    businessLocations: BusinessLocationsRequest[];
    selectedLocations: SelectionListItem[];
    translations: Record<string, string>;
}): SelectionListItem[] => {
    return businessLocations
        .map((locations) => {
            return locations.locations.filter((item) => item.canGrantAccess).map((location) => {
                return {
                    title: location.title,
                    subtitle: location.storefrontAddress
                        ? formatAddress(location.storefrontAddress, translations)
                        : '',
                    subtitle2: `ID: ${getBusinessLocationId(location.name)}`,
                    id: getBusinessLocationId(location?.name),
                    checked: selectedLocations
                        .map((location) => location.id)
                        .includes(getBusinessLocationId(location?.name)),
                    alreadyGrantedAccess: location.alreadyGrantedAccess,
                    canGrantAccess: location.canGrantAccess,
                };
            });
        })
        .flat();
};

const hasOnlyOneBusinessAccount = (data: BusinessAccountsRequest[] | undefined): boolean => {
    return data?.length === 1 && data[0].accounts.length === 1;
};

const isPersonalAccount = (account: SelectionListItem) => {
    return account.type === 'PERSONAL';
};

const isPersonalBusinessAccountSelectedButNoLocations = ({
    selectedAccounts,
    selectedLocations,
}: {
    selectedAccounts: SelectionListItem[];
    selectedLocations: SelectionListItem[];
}) => {
    const personalBusinessAccount = selectedAccounts.find((account) => isPersonalAccount(account));

    return personalBusinessAccount && selectedLocations.length === 0;
};

export {
    getBusinessAccountId,
    convertBusinessAccountsData,
    convertBusinessLocationsData,
    hasOnlyOneBusinessAccount,
    formatAddress,
    isPersonalAccount,
    isPersonalBusinessAccountSelectedButNoLocations,
};
